
import {
  iconAlertaAviso,
  iconAlertaErro,
  iconAlertaInformacao,
  iconAlertaSucesso
} from '@bsds-lib-component/icons/icons';
import { BsdsToast } from '@bsds-lib-component/react';
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';

import type { SystemMessageProps } from '../../types/componentType';
import { SystemMessageType as SystemMessageTypeEnum } from '../../types/componentType';

/**
 * SystemMessage component for displaying system messages using BsdsToast
 * 
 * Features:
 * - Supports both single string and array of messages
 * - Uses BsdsToast with different types for consistent styling
 * - Automatic dismissal after specified delay
 * - Displays as a toast notification overlay
 * - Built-in showMessage functionality
 * 
 * Note: This component uses the BsdsToast system for better UX and consistency.
 */
export const SystemMessage: React.FC<SystemMessageProps> = ({
  message,
  type = SystemMessageTypeEnum.INFO,
  show = true,
  onClick,
  onHide,
  autoHide = true,
  hideDelay = 8000,
}) => {
  const messageId = useId();
  const [isOpen, setIsOpen] = useState(false);

  const isArray = Array.isArray(message);
  const hasContent = Boolean(message) && (isArray ? message.length > 0 : String(message).trim().length > 0);
  const shouldDisplay = Boolean(show) && hasContent;

  const toastMessage = useMemo(() => {
    if (!hasContent) return '';
    if (isArray) {
      return `${message.map(error => `• ${error}`).join('\n')}`;
    }
    return message as string;
  }, [hasContent, isArray, message]);

  const displaySignature = useMemo(() => `${type}:${toastMessage}`, [type, toastMessage]);

  useEffect(() => {
    if (!shouldDisplay) {
      setIsOpen(false);
      return;
    }

    setIsOpen(false);
    const frame = requestAnimationFrame(() => setIsOpen(true));
    return () => cancelAnimationFrame(frame);
  }, [shouldDisplay, displaySignature]);

  const handleDidDismiss = useCallback(() => {
    setIsOpen(false);
    onHide?.();
  }, [onHide]);

  // Determine ARIA role and live region based on message type
  const getAriaAttributes = () => {
    switch (type) {
      case SystemMessageTypeEnum.ERROR:
        return {
          role: 'alert' as const,
          ariaLive: 'assertive' as const,
          ariaLabel: 'Mensagem de erro do sistema',
        };
      case SystemMessageTypeEnum.SUCCESS:
        return {
          role: 'status' as const,
          ariaLive: 'polite' as const,
          ariaLabel: 'Mensagem de sucesso do sistema',
        };
      case SystemMessageTypeEnum.WARNING:
        return {
          role: 'alert' as const,
          ariaLive: 'assertive' as const,
          ariaLabel: 'Mensagem de aviso do sistema',
        };
      case SystemMessageTypeEnum.INFO:
      default:
        return {
          role: 'status' as const,
          ariaLive: 'polite' as const,
          ariaLabel: 'Mensagem informativa do sistema',
        };
    }
  };

  const ariaAttributes = getAriaAttributes();

  if (!shouldDisplay) return null;

  const toastIcon = type === SystemMessageTypeEnum.ERROR ? iconAlertaErro :
    type === SystemMessageTypeEnum.SUCCESS ? iconAlertaSucesso :
      type === SystemMessageTypeEnum.WARNING ? iconAlertaAviso :
        type === SystemMessageTypeEnum.INFO ? iconAlertaInformacao :
          iconAlertaSucesso;

  // Define toast type class
  const getToastClass = () => {
    switch (type) {
      case SystemMessageTypeEnum.ERROR:
        return 'toast-error';
      case SystemMessageTypeEnum.SUCCESS:
        return 'toast-success';
      case SystemMessageTypeEnum.WARNING:
        return 'toast-warning';
      case SystemMessageTypeEnum.INFO:
        return 'toast-info';
      default:
        return 'toast-info';
    }
  };

  const toastClass = getToastClass();

  return (
    <div
      id={messageId}
      role={ariaAttributes.role}
      aria-live={ariaAttributes.ariaLive}
      aria-atomic="true"
      aria-label={ariaAttributes.ariaLabel}
    >
      <BsdsToast
        message={toastMessage}
        duration={autoHide ? hideDelay : 0}
        position="top"
        cssClass={toastClass}
        animated
        isOpen={isOpen}
        icon={toastIcon}
        platform="web"
        onClick={onClick}
        onBsdsToastDidDismiss={handleDidDismiss}
      />
    </div>
  );
};

export default SystemMessage;
